import {
  ADDRESS_ROUTE,
} from 'components/dynamic-page/route-matchers';
import { Breadcrumb, createBreadcrumbs } from 'components/breadcrumbs';
import { BreadcrumbType } from 'components/breadcrumbs/types';
import isUserAgentCrawler from 'utils/crawler-agent';
import { ProvinceAndState } from 'utils/province_or_state';
import Listing, { getListingById, getListingsBySlug, getPropertiesBySlug, ListingSerializableProperties } from 'data/listing';
import { createAddressHierarchyPath, parseAddressPath, ParsedAddressPath } from 'data/addresses';
import { ApiTimeouts } from 'constants/timeouts';
import { AddressType, SingleAddress, StandardStatus, type Property } from '@zoocasa/go-search';
import { isPending } from 'types/reso';
import { PENDING_STATUS } from 'contexts/preferences/listing-params/types';
import { isDevelopment, isEndToEndTest, isVercel } from 'utils/environment';
import { HttpStatusCode } from 'types/HttpStatusCode';
import generateCityFooterData from 'components/pages/area-listings/generate-dynamic-footer-data';
import { getComplianceRequirementFromRequest, shouldScrambleForProvider } from 'utils/compliance';
import { isDirectNavigation } from 'utils/next/navigation';
import { clearFeatureFlag, DEFAULT_FEATURE_FLAGS, FEATURE_FLAGS, hasFeatureFlag, setFeatureFlag } from './types';
import { removeUndefined } from 'utils/objects';
import { generateHreflangFromCanonicalUrl } from 'utils/urls/hreflangUrls';
import { generateCanonicalUrl } from 'utils/urls/canonicalUrls';
import { getHeadTags } from './seo';
import { SiteLanguage } from 'types/locales';
import { shouldShowAgentCards } from 'utils/agent-cards';
import { isQuebecListing } from 'utils/is-quebec-listing';
import { getTenantSeoConfig } from '@tenants/utils/configs';
import { getHideSquareFootage } from 'hooks/use-listings-compliance';
import { createVisibilityApi } from 'data/visibility';
import {
  extractHiddenListingSegmentFromPath,
  hiddenListingCodeFromSegment,
  parseListingIdFromHiddenListingCode,
} from 'utils/hidden-listing-url';

import type { GetServerSidePropsContext, GetServerSidePropsResult, Redirect } from 'next';
import type { ServerTranslationContext } from 'utils/translations/server-translation';
import type { LinkDataType } from 'components/home-page/internal-links';
import type { FeaturesType } from 'contexts/features';
import type { DynamicPageServerSideProps, HeadTags } from 'types/dynamic_page_types';
import type { TenantTypes } from '@tenants';
import type { IncomingMessage } from 'http';
import type { PriceHistory } from 'components/pages/address/public/components/price-history';
import type { ZoopraisalApi } from 'data/zoopraisal/api';
import type { AddressesApiType } from 'data/addresses/go-search/api';
import type { AddressPageProps, ComplianceProps, ExperimentProps, FeatureFlags, PropertyAppraisalProps, QAComparison, SecondaryInfoProps, StatsProps } from './address-page.types';

//#region Types

export type AddressPageServerSideProps = DynamicPageServerSideProps<AddressPageProps<ListingSerializableProperties>, typeof ADDRESS_ROUTE>;
type GetServerSidePropsReturnType = Promise<GetServerSidePropsResult<AddressPageServerSideProps>>;

//#endregion

//#region Constants

const DEFAULT_NOT_FOUND_RESPONSE: GetServerSidePropsResult<AddressPageServerSideProps> = { notFound: true };

function redactListingStreetAndCoordinatesForHiddenUrl(listing: Listing): void {
  listing.displayAddress = false;
  listing.position = null;
  listing.streetNumber = '';
  listing.streetName = '';
}

//#endregion

export type AddressPageParams = { addressPath: string[] };

//#region Main function
export default async function getServerSideProps(context: GetServerSidePropsContext<AddressPageParams>, features: FeaturesType, tenant: TenantTypes, addressesApi: AddressesApiType, appraisalApi: ZoopraisalApi, i18n?: ServerTranslationContext): GetServerSidePropsReturnType {
  const { params: { addressPath: addressPathArray }, req, query } = context;
  // Use the resolved locale from i18n context if available, otherwise fall back to context.locale
  const locale = i18n?.locale ?? context.locale ?? SiteLanguage.English;
  const addressPath = `/${addressPathArray.join('/')}`;
  const isCrawler = isUserAgentCrawler(req.headers);
  const preferredListingSource = typeof query.listingSource === 'string' ? query.listingSource : undefined;
  const requestedListingId = typeof query.listingId === 'string' ? parseInt(query.listingId, 10) : undefined;
  const parsedAddressPathFromUrl = parseAddressPath(addressPath); // This is the URl path as a parsed hierarchical representation of the requested addresses

  if (parsedAddressPathFromUrl.length === 0) {
    return DEFAULT_NOT_FOUND_RESPONSE; // If the address path is empty, we don't know what the user is looking for and we should return a 404.
  }

  // There's only one case where this path will have more than one ParsedAddressPath element and it only happens when we cannot infer if
  // the url was pointing to a neighbourhood or an address. This should have been handled by the middleware and at this point we should
  // have only an address.
  const requestedAddress = (parsedAddressPathFromUrl.length === 1) ? parsedAddressPathFromUrl[0] : parsedAddressPathFromUrl.filter(segment => segment.type === AddressType.ADDRESS_TYPE_ADDRESS)?.[0];

  if (!requestedAddress) {
    return DEFAULT_NOT_FOUND_RESPONSE; // If the address path is empty, we don't know what the user is looking for and we should return a 404.
  }

  const requestedAddressSlug: string = requestedAddress.slug;

  if (!requestedAddressSlug) {
    return DEFAULT_NOT_FOUND_RESPONSE; // If the address slug is empty, we don't know what the user is looking for and we should return a 404.
  }

  // Set up a custom API abort controller
  const controller = new AbortController();
  const goSearchTimeoutSignal = AbortSignal.timeout(ApiTimeouts.DEFAULT_TIMEOUT);
  const clientAppTimeoutSignal = AbortSignal.timeout(ApiTimeouts.GET_LISTING);

  try {
    const hiddenPathSegment = extractHiddenListingSegmentFromPath(addressPath);
    if (hiddenPathSegment) {
      const listingNumericId = parseListingIdFromHiddenListingCode(hiddenListingCodeFromSegment(hiddenPathSegment));
      if (!listingNumericId) {
        return DEFAULT_NOT_FOUND_RESPONSE;
      }

      const language = [SiteLanguage.FrenchCA.toLowerCase(), SiteLanguage.French.toLowerCase()].includes(locale.toLowerCase()) ? SiteLanguage.French : SiteLanguage.English;
      const goSearchPropertyTimeout = AbortSignal.timeout(ApiTimeouts.GO_SEARCH_PROPERTY);

      // Try go-search first (best fidelity). If local go-search doesn't have the listing (common in dev),
      // fall back to client-app so this route can still be validated in the browser.
      let property: Property | undefined;
      try {
        const visibility = await createVisibilityApi().getVisibilityById(
          listingNumericId,
          tenant.id,
          AbortSignal.any([controller.signal, goSearchTimeoutSignal])
        );

        const slugForProperty = visibility?.slug ?? requestedAddressSlug;
        const { properties } = await getPropertiesBySlug(
          { slug: slugForProperty },
          AbortSignal.any([controller.signal, goSearchPropertyTimeout]),
          undefined,
          tenant.id
        );
        property = properties[0];
      } catch {
        // ignore and fall back
      }

      const listing = property
        ? Listing.fromProperty(property, language)
        : await getListingById(listingNumericId, AbortSignal.any([controller.signal, clientAppTimeoutSignal]));

      if (!listing) {
        return DEFAULT_NOT_FOUND_RESPONSE;
      }

      // Hidden-address URLs always render as non-street-level regardless of upstream flags.
      redactListingStreetAndCoordinatesForHiddenUrl(listing);

      // Build breadcrumbs from the listing's canonical address hierarchy.
      // The URL can contain an arbitrary neighbourhood segment (e.g. "alamosa") that is not a real slug.
      const propertyHierarchyRows = (property?.addresses ?? []).filter(
        a => a.addressType !== AddressType.ADDRESS_TYPE_ADDRESS
      );
      const hierarchyRows = propertyHierarchyRows.length
        ? propertyHierarchyRows
        : await addressesApi.getAddressesBySlug(
          requestedAddress.subDivision?.slug ?? '',
          AbortSignal.any([controller.signal, goSearchTimeoutSignal])
        );
      if (!hierarchyRows.length) {
        return DEFAULT_NOT_FOUND_RESPONSE;
      }

      const leaf = hierarchyRows[hierarchyRows.length - 1];
      const hiddenListingPlaceholder: SingleAddress = {
        addressType: AddressType.ADDRESS_TYPE_ADDRESS,
        slug: requestedAddressSlug,
        label: '',
        address: '',
        country: leaf.country,
        countryCode: leaf.countryCode,
        provinceOrState: leaf.provinceOrState,
        subDivision: leaf.subDivision,
        neighbourhood: leaf.neighbourhood,
      };

      const electedAddressPageAddressHierarchy = createAddressHierarchyPath([...hierarchyRows, hiddenListingPlaceholder]);
      if (!electedAddressPageAddressHierarchy) {
        return DEFAULT_NOT_FOUND_RESPONSE;
      }

      const listings: Listing[] = [listing];
      const listingSource: 'go-search' | 'client-app-fallback' = property ? 'go-search' : 'client-app-fallback';

      const breadcrumbs: readonly Breadcrumb[] = (createBreadcrumbs(electedAddressPageAddressHierarchy) || []).filter(
        b => b.type !== BreadcrumbType.BREADCRUMB_TYPE_ADDRESS
      );

      const isExternalNavigation = isDirectNavigation(req);
      const featureFlags: FeatureFlags['featureFlags'] = {
        flags: DEFAULT_FEATURE_FLAGS,
        internalLinks: [],
      };

      if (isCrawler || isExternalNavigation) {
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.USE_SIMILAR_LISTINGS_PRIORITY_MODE);
      }

      if (!listing.isRental && listing.isAvailable) {
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PROPERTY_APPRAISAL_ENABLED);
      }

      if (shouldShowAgentCards(listing, tenant.id)) {
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.AGENT_CARDS_ENABLED);
      }

      if (isQuebecListing(listing.province)) {
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED);
      }

      if (listing.hidePriceHistory) {
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED);
      }

      if (isCrawler) {
        const internalLinks: LinkDataType[] = await generateCityInternalLinks(electedAddressPageAddressHierarchy.subDivision, tenant) || [];
        featureFlags.internalLinks = internalLinks;
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.INTERNAL_LINKS_ACTIVE);
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.USE_CAROUSEL_PRIORITY_MODE);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.CHARTS_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.SECONDARY_INFO_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PROVIDER_DISCLAIMER_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.MY_LINK_MY_LEAD_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.WALK_SCORE_ENABLED);
      }

      const secondaryInfo: SecondaryInfoProps['secondaryInfo'] = {
        schools: [],
        neighbourhoodDemographics: null,
      };

      const experiments: ExperimentProps['experiments'] = {
        soldListingExperimentId: null,
      };

      const stats: StatsProps['stats'] = {
        municipality: null,
        neighbourhood: null,
      };

      const appraisal: PropertyAppraisalProps['appraisal'] = {
        appraisalResult: null,
        sellingRange: null,
      };

      if (hasFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PROPERTY_APPRAISAL_ENABLED)) {
        const zoopraisalTimeoutSignal = AbortSignal.timeout(ApiTimeouts.DEFAULT_TIMEOUT);
        const appraisalResult = await appraisalApi.getZoopraisal({ listing }, AbortSignal.any([controller.signal, zoopraisalTimeoutSignal]));
        if (appraisalResult) {
          appraisal.appraisalResult = appraisalResult.toJSON();
          appraisal.sellingRange = appraisalApi.calculateSellingRange(listing.price, appraisalResult.averagePrice);
        }
      }

      const canonicalUrl = generateCanonicalUrl({
        asPath: addressPath,
        baseHost: tenant.theme.schemaUrl,
        locale,
        tenantId: tenant.id,
      });
      const hrefLangs = generateHreflangFromCanonicalUrl({
        canonicalUrl,
        locale: context.locale || tenant.localization.defaultLanguage,
        tenantId: tenant.id,
      });
      const headTags: HeadTags['headTags'] = getHeadTags({
        tenantName: tenant.theme.name,
        schemaUrl: tenant.theme.schemaUrl,
        listing,
        breadcrumbs,
        hrefLangs,
        locale,
        tenantId: tenant.id,
        seoConfig: getTenantSeoConfig(tenant),
        t: i18n?.t,
      });

      const complianceRequirement = getComplianceRequirementFromRequest(req, listing.providerId);

      const compliance: ComplianceProps['compliance'] = {
        complianceRequirement,
        showTermsOfUseBanner: false,
        shouldScramble: shouldScrambleForProvider(listing.providerId, complianceRequirement),
        hideSquareFootage: getHideSquareFootage(listing.providerId, listing.standardStatus),
      };

      return {
        props: {
          routeName: ADDRESS_ROUTE,
          props: {
            address: removeUndefined(electedAddressPageAddressHierarchy.address),
            activeListing: { listing: listing.toJSON() },
            history: listings.map(l => listingToPriceHistoryJson(l, req)),
            breadcrumbs,
            featureFlags,
            compliance,
            experiments,
            stats,
            appraisal,
            tenantId: tenant.id,
            features,
            headTags,
            secondaryInfo,
            listingSource,
            qaComparison: null,
          },
        },
      };
    }

    const requestedAddressPageAddress = await addressesApi.getAddressesBySlug(requestedAddressSlug, AbortSignal.any([controller.signal, goSearchTimeoutSignal]));
    if (requestedAddressPageAddress?.length > 0) {
      let listing: Listing | null = null;
      let listings: Listing[] = [];

      const electedAddressPageAddressHierarchy = createAddressHierarchyPath(requestedAddressPageAddress);
      if (!electedAddressPageAddressHierarchy) {
        return DEFAULT_NOT_FOUND_RESPONSE; // If we cannot create an address hierarchy path, we don't know what the user is looking for and we should return a 404.
      }

      if (electedAddressPageAddressHierarchy.type !== AddressType.ADDRESS_TYPE_ADDRESS) {
        console.error(`address page received a request to display an address that is not supported: ${requestedAddressSlug}`);
        return DEFAULT_NOT_FOUND_RESPONSE; // If the requested address is not an address we should not try to open in an address page. This should never happen. This is a bug.
      }

      // FIXME: Find a better way to determine the language.
      const language = [SiteLanguage.FrenchCA.toLowerCase(), SiteLanguage.French.toLowerCase()].includes(locale.toLowerCase()) ? SiteLanguage.French : SiteLanguage.English;

      const slug = electedAddressPageAddressHierarchy.slug;
      let listingSource: 'go-search' | 'client-app-fallback' = 'go-search';
      let qaComparison: QAComparison | undefined;

      if (isEndToEndTest) {
        // E2E tests: use client-app only to avoid go-search data parity issues in test assertions
        const availableListings = await getListingsBySlug({ slug, getDeleted: false }, AbortSignal.any([controller.signal, clientAppTimeoutSignal]), language);
        listings = availableListings?.listings || [];
        listingSource = 'client-app-fallback';
      } else if (isVercel) {
        // Vercel preview only: fetch both sources in parallel for QA dual-tab comparison
        const [goSearchResult, clientAppResult] = await Promise.allSettled([
          getPropertiesBySlug({ slug }, AbortSignal.any([controller.signal, goSearchTimeoutSignal]), undefined, tenant.id),
          getListingsBySlug({ slug, getDeleted: false }, AbortSignal.any([controller.signal, clientAppTimeoutSignal]), language),
        ]);

        let goSearchListings: Listing[] = [];
        let clientAppListings: Listing[] = [];
        let goSearchError: string | undefined;
        let clientAppError: string | undefined;

        if (goSearchResult.status === 'fulfilled' && goSearchResult.value.properties.length > 0) {
          goSearchListings = goSearchResult.value.properties.map(p => Listing.fromProperty(p, language));
        } else if (goSearchResult.status === 'rejected') {
          goSearchError = goSearchResult.reason?.message || 'Failed to fetch from go-search';
        } else {
          goSearchError = 'No listings returned';
        }

        if (clientAppResult.status === 'fulfilled') {
          clientAppListings = clientAppResult.value?.listings || [];
          if (clientAppListings.length === 0) {
            clientAppError = 'No listings returned';
          }
        } else {
          clientAppError = clientAppResult.reason?.message || 'Failed to fetch from client-app';
        }

        listings = clientAppListings;
        listingSource = 'client-app-fallback';

        const goSearchActiveListing = getLatestNonDeletedListing(goSearchListings);
        const clientAppActiveListing = getLatestNonDeletedListing(clientAppListings);

        qaComparison = {
          goSearch: {
            activeListing: goSearchActiveListing ? { listing: goSearchActiveListing.toJSON() } : null,
            history: goSearchListings.map(l => listingToPriceHistoryJson(l, req)),
            error: goSearchError,
          },
          clientApp: {
            activeListing: clientAppActiveListing ? { listing: clientAppActiveListing.toJSON() } : null,
            history: clientAppListings.map(l => listingToPriceHistoryJson(l, req)),
            error: clientAppError,
          },
        };
      } else if (preferredListingSource === 'client-app-fallback') {
        // Modal already determined go-search couldn't serve this listing — skip go-search entirely
        listingSource = 'client-app-fallback';
        try {
          const availableListings = await getListingsBySlug({ slug, getDeleted: false }, AbortSignal.any([controller.signal, clientAppTimeoutSignal]), language);
          listings = availableListings?.listings || [];
        } catch (err) {
          console.warn(`client-app V3 failed for slug: ${slug}`, err instanceof Error ? err.message : err);
        }
      } else {
        // All environments (dev, staging, production): go-search primary with client-app V3 fallback
        const goSearchPropertyTimeout = AbortSignal.timeout(ApiTimeouts.GO_SEARCH_PROPERTY);
        try {
          const { properties } = await getPropertiesBySlug(
            { slug },
            AbortSignal.any([controller.signal, goSearchPropertyTimeout]),
            undefined,
            tenant.id
          );

          if (properties.length > 0) {
            listings = properties.map(p => Listing.fromProperty(p, language));
            listingSource = 'go-search';

            if (requestedListingId && requestedListingId > 0) {
              const hasRequestedListing = listings.some(l => Number(l.id) === requestedListingId);
              if (!hasRequestedListing) {
                listings = [];
              }
            }
          }
        } catch (err) {
          console.warn(`go-search failed for slug: ${slug}, falling back to V3`, err instanceof Error ? err.message : err);
        }

        if (listings.length === 0) {
          listingSource = 'client-app-fallback';
          try {
            const availableListings = await getListingsBySlug({ slug, getDeleted: false }, AbortSignal.any([controller.signal, clientAppTimeoutSignal]), language);
            listings = availableListings?.listings || [];
          } catch (err) {
            console.warn(`client-app V3 also failed for slug: ${slug}`, err instanceof Error ? err.message : err);
          }
        }
      }

      // New Relic tracking (both prod and non-prod)
      try {
        if (typeof window === 'undefined') {
          const nr = await import(/* webpackIgnore: true */ 'newrelic');
          nr.default.addCustomAttributes({ listingSource, listingSourceSlug: slug, ...(requestedListingId && { requestedListingId }) });
        }
      } catch {
        // New Relic may not be available on Vercel preview — safe to ignore
      }
      if (listings.length === 0) {
        // If no listings are found for the address, we will try to redirect the user to the area page of the address as a fallback to avoid a 404.
        return createAreaPageRedirection(requestedAddress, req.headers.host || tenant.hosts[0]);
      }
      listing = (requestedListingId && requestedListingId > 0
        ? listings.find(l => Number(l.id) === requestedListingId)
        : null) || getLatestNonDeletedListing(listings);

      if (listing === null || listing === undefined) {
        // If we couldn't find a listing for the address because it doesn't exist or an error occurred, we will try to
        // redirect the user to the area page of the address as a fallback to avoid a 404.
        return createAreaPageRedirection(requestedAddress, req.headers.host || tenant.hosts[0]);
      }

      const breadcrumbs: readonly Breadcrumb[] = createBreadcrumbs(electedAddressPageAddressHierarchy) || [];
      // Check if this is a external or internal navigation
      const isExternalNavigation = isDirectNavigation(req);
      const featureFlags: FeatureFlags['featureFlags'] = {
        flags: DEFAULT_FEATURE_FLAGS,
        internalLinks: [],
      };

      // If the request is from a crawler or a direct navigation, we will use the similar listings priority mode to move the similar listings to the top of the page.
      if (isCrawler || isExternalNavigation) {
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.USE_SIMILAR_LISTINGS_PRIORITY_MODE);
      }

      // It's only necessary to show the property appraisal if the listing is not a rental and is available.
      if (!listing.isRental && listing.isAvailable) {
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PROPERTY_APPRAISAL_ENABLED);
      }

      // Show agent cards on address/listing page if conditions are met
      if (shouldShowAgentCards(listing, tenant.id)) {
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.AGENT_CARDS_ENABLED);
      }

      // Hide price history for Quebec listings for regional compliance
      if (isQuebecListing(listing.province)) {
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED);
      }

      // IDX compliance: suppress sections based on MLS-level restrictions
      if (listing.hidePriceHistory) {
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED);
      }

      if (isCrawler) {
        const internalLinks: LinkDataType[] = await generateCityInternalLinks(electedAddressPageAddressHierarchy.subDivision, tenant) || [];
        featureFlags.internalLinks = internalLinks;
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.INTERNAL_LINKS_ACTIVE);
        featureFlags.flags = setFeatureFlag(featureFlags.flags, FEATURE_FLAGS.USE_CAROUSEL_PRIORITY_MODE);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.CHARTS_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.SECONDARY_INFO_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PROVIDER_DISCLAIMER_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.MY_LINK_MY_LEAD_ENABLED);
        featureFlags.flags = clearFeatureFlag(featureFlags.flags, FEATURE_FLAGS.WALK_SCORE_ENABLED);
      }

      const secondaryInfo: SecondaryInfoProps['secondaryInfo'] = {
        schools: [],
        neighbourhoodDemographics: null,
      };

      const experiments: ExperimentProps['experiments'] = {
        soldListingExperimentId: null,
      };

      const stats: StatsProps['stats'] = {
        municipality: null,
        neighbourhood: null,
      };

      const appraisal: PropertyAppraisalProps['appraisal'] = {
        appraisalResult: null,
        sellingRange: null,
      };

      if (hasFeatureFlag(featureFlags.flags, FEATURE_FLAGS.PROPERTY_APPRAISAL_ENABLED)) {
        const zoopraisalTimeoutSignal = AbortSignal.timeout(ApiTimeouts.DEFAULT_TIMEOUT);
        const appraisalResult = await appraisalApi.getZoopraisal({ listing }, AbortSignal.any([controller.signal, zoopraisalTimeoutSignal]));
        if (appraisalResult) {
          appraisal.appraisalResult = appraisalResult.toJSON();
          appraisal.sellingRange = appraisalApi.calculateSellingRange(listing.price, appraisalResult.averagePrice);
        }
      }

      const canonicalUrl = generateCanonicalUrl({
        asPath: addressPath,
        baseHost: tenant.theme.schemaUrl,
        locale,
        tenantId: tenant.id,
      });
      const hrefLangs = generateHreflangFromCanonicalUrl({
        canonicalUrl,
        locale: context.locale || tenant.localization.defaultLanguage,
        tenantId: tenant.id,
      });
      const headTags: HeadTags['headTags'] = getHeadTags({
        tenantName: tenant.theme.name,
        schemaUrl: tenant.theme.schemaUrl,
        listing,
        breadcrumbs,
        hrefLangs,
        locale,
        tenantId: tenant.id,
        seoConfig: getTenantSeoConfig(tenant),
        t: i18n?.t,
      });

      const complianceRequirement = getComplianceRequirementFromRequest(req, listing.providerId);

      const compliance: ComplianceProps['compliance'] = {
        complianceRequirement,
        showTermsOfUseBanner: false,
        shouldScramble: shouldScrambleForProvider(listing.providerId, complianceRequirement),
        hideSquareFootage: getHideSquareFootage(listing.providerId, listing.standardStatus),
      };

      return {
        props: {
          routeName: ADDRESS_ROUTE,
          props: {
            address: removeUndefined(electedAddressPageAddressHierarchy.address),
            activeListing: { listing: listing.toJSON() },
            history: listings.map(listing => listingToPriceHistoryJson(listing, req)),
            breadcrumbs,
            featureFlags,
            compliance,
            experiments,
            stats,
            appraisal,
            tenantId: tenant.id,
            features,
            headTags,
            secondaryInfo,
            listingSource,
            qaComparison: qaComparison ?? null,
          },
        },
      };
    }

    // If the address does not exist at all, we should return a 404 as we don't know if there is an area page we can fallback to.
    return DEFAULT_NOT_FOUND_RESPONSE;
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      console.error('getServerSideProps was aborted while fetching address data for address:', requestedAddressSlug, err);
    } else if (err instanceof Error && err.name === 'TimeoutError') {
      console.error('getServerSideProps timed out while fetching address data for address:', requestedAddressSlug, err);
    } else {
      console.error('getServerSideProps crashed while fetching address data:', err);
    }
    throw err;
  }
}
//#endregion

//#region Helper functions

/**
 * Creates a redirection to the area page for the given address.
 *
 * This function is used when a user requests an address page but no listings are found
 * for that specific address. Instead of showing a 404 error, it redirects the user to
 * the corresponding area page (e.g., from a specific address to its neighbourhood or city).
 *
 * The function works by removing the last segment from the address path, which typically
 * represents the most specific location (like a street address), and redirecting to the
 * broader area context.
 *
 * @param address The parsed address path object containing the hierarchical address information
 * @returns A Next.js redirect object with status code 307 (Temporary Redirect) and the destination
 *          URL, or a 404 response if the address is invalid or doesn't have enough path segments
 *
 * @example
 * // For an address like "/toronto-on-real-estate/downtown/123-main-st"
 * // This would redirect to "/toronto-on-real-estate/downtown"
 * const redirect = createAreaPageRedirection(parsedAddress);
 */
function createAreaPageRedirection(address: ParsedAddressPath, baseUrl: string) {
  // If the address is not valid, we don't know what the user is looking for and we should return a 404.
  if (!address || !address.acceptedPathSegments || address.acceptedPathSegments.length < 2 || address.type !== AddressType.ADDRESS_TYPE_ADDRESS) {
    return DEFAULT_NOT_FOUND_RESPONSE;
  }

  const sanitizedBaseUrl = baseUrl.startsWith('http') ? baseUrl : isDevelopment ? `http://${baseUrl}` : `https://${baseUrl}`;

  // We need to remove the last segment of the path to get the destination.
  // The last segment is the city, and we need to remove it to get the destination.
  const destination = address.acceptedPathSegments.slice(0, address.acceptedPathSegments.length - 1).join('/');
  const redirectUrl = new URL(`/${destination}`, sanitizedBaseUrl);
  const redirect: Redirect = {
    statusCode: HttpStatusCode.TEMPORARY_REDIRECT,
    destination: redirectUrl.toString(),
  };
  return { redirect };
}

async function generateCityInternalLinks(city: SingleAddress, tenant: TenantTypes) {
  if (city.addressType === AddressType.ADDRESS_TYPE_SUB_DIVISION) {
    const slug = city.slug;
    const area = city.subDivision || '';
    const province = city.provinceOrState?.toUpperCase() as keyof typeof ProvinceAndState;
    const data = await generateCityFooterData(area, slug, tenant, province, 10, false, false);
    return data;
  }
}


function listingToPriceHistoryJson(listing: Listing, request: IncomingMessage): PriceHistory<ListingSerializableProperties> {
  const listingJson = listing.toJSON();
  return {
    id: listingJson.id,
    isRental: listingJson.isRental,
    soldPrice: listingJson.soldPrice,
    soldAt: listingJson.soldAt,
    expiredAt: listingJson.expiredAt,
    price: listingJson.price,
    addedAt:  listingJson?.addedAt,
    standardStatus: listingJson.standardStatus,
    mlsStatus: listingJson.mlsStatus,
    mlsNum: listingJson.mlsNum,
    imageUrl: listingJson.imageUrl,
    thumbnailUrl: listingJson.thumbnailUrl,
    imageDesc: listingJson.imageDesc,
    isAvailable: listing.isAvailable,
    addressPath: listing.addressPath,
    providerId: listing.providerId,
    complianceRequirement: getComplianceRequirementFromRequest(request, listing.providerId),
  };
}

/**
 * Retrieves the most relevant listing from an array of listings based on availability and recency.
 *
 * This function implements a priority-based selection algorithm to find the best listing to display
 * when multiple listings exist for the same address. The selection follows this priority order:
 *
 * 1. **Available for Sale**: First priority goes to listings that are currently available for purchase
 *    (not rentals and not off-market)
 * 2. **Pending Listings**: If no available listings exist, returns the most recently added pending listing
 *    (sorted by `addedAt` date in descending order)
 * 3. **Non-Off-Market Listings**: If no pending listings exist, returns the most recently added
 *    non-off-market listing (sorted by `addedAt` date in descending order)
 * 4. **Fallback**: If no non-off-market listings exist, returns the first listing in the array
 *
 * @param listings An array of Listing objects to search through
 * @returns The most relevant listing based on the priority algorithm, or `undefined` if the array is empty
 *
 * @example
 * ```typescript
 * const listings = [
 *   { id: 1, isAvailable: true, isRental: false, addedAt: new Date('2024-01-01') },
 *   { id: 2, isAvailable: false, isOffMarket: false, addedAt: new Date('2024-02-01') },
 *   { id: 3, isAvailable: false, isOffMarket: true, addedAt: new Date('2024-03-01') }
 * ];
 *
 * const bestListing = getLatestNonDeletedListing(listings);
 * // Returns listing with id: 1 (available for sale)
 * ```
 *
 * @example
 * ```typescript
 * const listings = [
 *   { id: 1, isAvailable: false, standardStatus: STANDARD_STATUS_PENDING, addedAt: new Date('2024-01-01') },
 *   { id: 2, isAvailable: false, standardStatus: STANDARD_STATUS_EXPIRED, addedAt: new Date('2024-03-01') }
 * ];
 *
 * const bestListing = getLatestNonDeletedListing(listings);
 * // Returns listing with id: 1 (pending listing takes priority over expired)
 * ```
 */
const getLatestNonDeletedListing = (listings: Listing[]): Listing | undefined => {
  if (!listings || listings.length === 0) {
    return undefined;
  }
  const listingIsAvailableAndForSale = listings.find(listing => listing.isAvailable && !listing.isRental);
  if (listingIsAvailableAndForSale) {
    return listingIsAvailableAndForSale;
  }
  // If no available listings exist, check for pending listings (sorted by most recent first)
  // Check both standardStatus and status field (fallback) similar to how isAvailable works
  const pendingListings = listings
    .filter(listing => {
      const isPendingByStandardStatus = listing.standardStatus && listing.standardStatus !== StandardStatus.UNRECOGNIZED && isPending(listing.standardStatus);
      const isPendingByStatus = listing.status === PENDING_STATUS;
      return isPendingByStandardStatus || isPendingByStatus;
    })
    .sort((a, b) => a.addedAt < b.addedAt ? 1 : -1);
  if (pendingListings.length > 0) {
    return pendingListings[0];
  }
  // If no pending listings exist, we will return the most recent non-off-market listing.
  const firstNonDeletedListing = listings.sort((a, b) => a.addedAt < b.addedAt ? 1 : -1).find(listing => listing.standardStatus !== StandardStatus.STANDARD_STATUS_DELETE);
  if (firstNonDeletedListing) {
    return firstNonDeletedListing;
  }
  // If no non-off-market listings exist, we will return the first listing in the array.
  return listings[0];
};

export { getLatestNonDeletedListing };
//#endregion
